import { Injectable } from '@angular/core';
import { ConfigService } from './config.service';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import LaunchDarkly, { LDContext } from 'launchdarkly-js-client-sdk';
import {distinctUntilChanged, map} from "rxjs/operators";

enum ComparisonType {
	GREATER = 'GREATER',
	GREATER_OR_EQUAL = 'GREATER_OR_EQUAL',
	LESS_OR_EQUAL = 'LESS_OR_EQUAL',
	LESS = 'LESS',
	EQUAL = 'EQUAL'
}

const devEnvs = ['dev'];

@Injectable({
	providedIn: 'root'
})
export class LaunchDarklyService {
	private ldClient: any;
	private flagsSubject = new BehaviorSubject<any>({});
	flags$ = this.flagsSubject.asObservable();


	constructor(
    private configService: ConfigService
  ) {
		this.initialize();
	}

	/**
	 * Returns a reactive observable of the flag value associated with the featureKey within the 'intercab_releases' container
	 */
	public observeReleaseFlagFromKey(featureKey: string): Observable<boolean> {
		return this.flags$.pipe(
			map((flags) => {
				const containerValue = Array.isArray(flags['intercab_releases']) ? flags['intercab_releases'] : [];
				const releaseEntry = containerValue.find((entry: any) => entry.key === featureKey);

				if (!releaseEntry || typeof releaseEntry.release !== 'string') {
					return false;
				}

				const releaseFlagValue = flags[releaseEntry.release];
				return !!releaseFlagValue;
			}),
			distinctUntilChanged()
		);
	}

	/**
	 * Returns an observable with all flags mapped by featureKey => boolean value
	 */
	public observeMultipleReleaseFlags(featureKeys: string[]): Observable<Record<string, boolean>> {
		const observables = featureKeys.map(key =>
			this.observeReleaseFlagFromKey(key).pipe(
				map(value => ({ key, value }))
			)
		);

		return combineLatest(observables).pipe(
			map(results => {
				const mapObj: Record<string, boolean> = {};
				results.forEach(({ key, value }) => {
					mapObj[key] = value;
				});
				return mapObj;
			})
		);
	}


	public greaterThan(flag: string): boolean {
		return this.getComparedFlagsValues(flag, ComparisonType.GREATER);
	}

	public greaterOrEqualThan(flag: string): boolean {
		return this.getComparedFlagsValues(flag, ComparisonType.GREATER_OR_EQUAL);
	}

	public lessOrEqualThan(flag: string): boolean {
		return this.getComparedFlagsValues(flag, ComparisonType.LESS_OR_EQUAL);
	}

	public lessThan(flag: string): boolean {
		return this.getComparedFlagsValues(flag, ComparisonType.LESS);
	}

	public equal(flag: string): boolean {
		return this.getComparedFlagsValues(flag, ComparisonType.EQUAL);
	}

	private async initialize(): Promise<void> {
		const clientSideId = this.configService._config.ldClientId;
    if(clientSideId) {

      const context: LDContext = {
        kind: 'user',
        key: 'PCAB2-User'
      };

      this.ldClient = LaunchDarkly.initialize(clientSideId, context);

      this.ldClient.on('change', (changes: any) => {
        const updatedFlags = { ...this.flagsSubject.value };
        Object.keys(changes).forEach((flagKey) => {
          updatedFlags[flagKey] = changes[flagKey].current;
        });
        this.flagsSubject.next(updatedFlags);
        console.log('Flags atualizadas:', updatedFlags);
      });

      this.ldClient.on('ready', () => {
        console.log('LaunchDarkly client ready!');
        this.flagsSubject.next(this.ldClient.allFlags());
      });

      this.ldClient.on('failed', (error: any) => {
        console.error('LaunchDarkly failed to initialize:', error);
      });
    }
	}

	private getComparedFlagsValues(flag: string, type: ComparisonType): boolean {
		const versionNumber = this.getVersionNumber(flag);
		let validValue = false;

		this.flags$.forEach((flags) => {
			const validFlags: Map<string, boolean> = this.getFlagNameWithoutVersionNumber(flags, flag);
			if (validFlags.has(flag)) {
				validFlags.forEach((flagValue, flagName) => {
					const versionNumberActive = this.getVersionNumber(flagName);
					if (flagValue && versionNumberActive != null && flag != null) {
						if ((type == ComparisonType.GREATER || type == ComparisonType.GREATER_OR_EQUAL) && versionNumberActive > versionNumber) {
							validValue = true;
						}

						if ((type == ComparisonType.LESS || type == ComparisonType.LESS_OR_EQUAL) && versionNumberActive < versionNumber) {
							validValue = true;
						}

						if ((type == ComparisonType.EQUAL || type == ComparisonType.GREATER_OR_EQUAL || type == ComparisonType.LESS_OR_EQUAL) && versionNumber == versionNumberActive) {
							validValue = true;
						}
					}
				});
			}
		});

		return validValue;
	}

	private getVersionNumber(flag: string): number {
		const version = flag.replace(/\D+/g, '');
		return version ? Number(version) : 0;
	}

	private getFlagNameWithoutVersionNumber(flags: any, flag: string): Map<string, boolean> {
		const noNumbers = flag.replace(/\d+/g, '');
		const validFlags = new Map<string, boolean>();

		Object.keys(flags).forEach((flagVersion) => {
			if (flagVersion.replace(/\d+/g, '').includes(noNumbers)) {
				validFlags.set(flagVersion, flags[flagVersion] || false);
			}
		});

		return validFlags;
	}
}
